import { forwardRef } from 'react';

import { Switch as BaseSwitch } from '@base-ui/react/switch';

import { cn } from '../utils';
import {
  switchAlignRecipe,
  switchControlRecipe,
  switchLabelRecipe,
  switchRootRecipe,
  switchThumbRecipe,
} from './Switch.recipe';

import type { SwitchProps } from './types';

/**
 * Design System 2.0 switch.
 *
 * Ported from Figma node 593:39343 (file 5aSkE0U4TeWbuIZDvccHjc). Props mirror the variant axes that
 * are real inputs — `checked`, `disabled`, `label` — while `State` is observed from the DOM rather
 * than passed, because Base UI already tracks it, and `Mode` is not a prop at all.
 *
 * Theme is not a prop. Colours resolve from the nearest `.dark` ancestor through the token layer, so
 * the same markup is correct in both themes. In this component's case that layer barely has to do
 * anything: only the focus halo and the label change between the two — see `.ds-switch`.
 *
 * ── Use this instead of `Checkbox` when the change is immediate ────────────────────────────────
 *
 * The two are not interchangeable, and the difference is not visual. A switch is announced with
 * `role="switch"`, which tells a screen-reader user the setting takes effect as soon as it is
 * flipped; a checkbox states a value to be submitted later. Every one of the design's own examples
 * is a community setting that saves on change, which is why they are switches.
 *
 * ── Structure ──────────────────────────────────────────────────────────────────────────────────
 *
 * The wrapper is a real `<label>`, which is Base UI's documented shape: the hidden `<input>` lives
 * inside `Switch.Root`, so wrapping both it and the text makes the text a click target and gives
 * the control its accessible name with no `id` plumbing.
 *
 * Between the two sits a 20px alignment shim, which exists so the track can stay exactly 36x18 and
 * still centre on the first line of a wrapping label. `Switch.recipe` has the arithmetic.
 *
 * Geometry, the state machine, and why the focus ring is an outline here and a background on the
 * checkbox, are all in `Switch.recipe`.
 */
export const Switch = forwardRef<HTMLElement, SwitchProps>(
  ({ label, className, rootClassName, labelClassName, disabled, ...rest }, ref) => (
    <label className={cn(switchRootRecipe({ disabled }), rootClassName)}>
      <span className={switchAlignRecipe()}>
        {/* `data-part` names the track so a test can assert on it without depending on a styling
            class, as `TextInput` does for its bordered box. The track IS the root here, so this is
            the one part that doubles as the focusable element. */}
        <BaseSwitch.Root
          ref={ref}
          className={cn(switchControlRecipe(), className)}
          data-part="track"
          disabled={disabled}
          {...rest}
        >
          <BaseSwitch.Thumb className={switchThumbRecipe()} data-part="thumb" />
        </BaseSwitch.Root>
      </span>

      {label !== undefined && <span className={cn(switchLabelRecipe({ disabled }), labelClassName)}>{label}</span>}
    </label>
  )
);
